import SentrySingleton from '../services/SentrySingleton';
import { getSentryTrackingUserContext } from './sentryTrackingConfig';

/**
 * Centralized utility for capturing errors to Sentry with proper context
 */

/**
 * Get the initialized Sentry instance from singleton
 */
const getSentry = () => {
  const sentrySingleton = SentrySingleton.getInstance();
  return sentrySingleton.getSentry();
};

/**
 * Get current user context (single reader via sentryTrackingConfig)
 */
const getCurrentUserContext = () => getSentryTrackingUserContext();

/**
 * Get current user ID (backward compatibility)
 */
const getCurrentUserId = (): string | undefined => {
  return getSentryTrackingUserContext().userId;
};

export interface GraphQLErrorContext {
  operationName?: string;
  variables?: Record<string, any>;
  query?: string;
  message?: string;
  locations?: any[];
  path?: any[];
  extensions?: Record<string, any>;
}

export interface BusinessLogicContext {
  component?: string;
  action?: string;
  userId?: string;
  additionalData?: Record<string, any>;
}

/**
 * Capture GraphQL errors to Sentry with rich context
 */
export const captureGraphQLError = (error: Error | string, context: GraphQLErrorContext = {}, userId?: string) => {
  try {
    const Sentry = getSentry();
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'graphql');
      scope.setTag('operation_name', context.operationName || 'unknown');
      
      // Use passed userId or fallback to Sentry context
      const finalUserId = userId || getCurrentUserId();
      if (finalUserId) {
        scope.setTag('user_id', finalUserId);
      }
      const { accountId, accountName, schoolId, schoolName } = getSentryTrackingUserContext();
      
      if (context.operationName) {
        scope.setTag('graphql_operation', context.operationName);
      }
      
      scope.setContext('graphql_error', {
        message: context.message,
        locations: context.locations,
        path: context.path,
        extensions: context.extensions,
        variables: context.variables,
        query: context.query,
        userId, // Include user ID in context
        accountId,
        accountName,
        schoolId,
        schoolName,
      });
      
      scope.setLevel('error');
      
      const errorToCapture = error instanceof Error ? error : new Error(String(error));
      Sentry.captureException(errorToCapture);
    });
  } catch (sentryError) {
    console.error('[Sentry] Error capturing GraphQL error:', sentryError);
  }
};

/**
 * Capture network errors to Sentry
 */
export const captureNetworkError = (error: Error, context: { operationName?: string; statusCode?: number } = {}, userId?: string) => {
  try {
    const Sentry = getSentry();
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'network');
      scope.setTag('operation_name', context.operationName || 'unknown');
      
      // Use passed userId or fallback to Sentry context
      const finalUserId = userId || getCurrentUserId();
      if (finalUserId) {
        scope.setTag('user_id', finalUserId);
      }
      const { accountId, accountName, schoolId, schoolName } = getSentryTrackingUserContext();
      if (accountId) {
        scope.setTag('account_id', String(accountId));
        if (accountName) scope.setTag('account_name', accountName);
      }
      if (schoolId) {
        scope.setTag('school_id', String(schoolId));
        if (schoolName) scope.setTag('school_name', schoolName);
      }
      
      if (context.statusCode) {
        scope.setTag('status_code', context.statusCode);
      }
      
      scope.setContext('network_error', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        statusCode: context.statusCode,
        userId: finalUserId,
        accountId,
        accountName,
        schoolId,
        schoolName,
      });
      
      scope.setLevel('error');
      Sentry.captureException(error);
    });
  } catch (sentryError) {
    console.error('[Sentry] Error capturing network error:', sentryError);
  }
};

/**
 * Capture business logic errors to Sentry
 */
export const captureBusinessLogicError = (error: Error | string, context: BusinessLogicContext = {}) => {
  try {
    const Sentry = getSentry();
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'business_logic');
      
      if (context.component) {
        scope.setTag('component', context.component);
      }
      
      if (context.action) {
        scope.setTag('action', context.action);
      }
      
      // Use provided userId or automatically get current user ID
      const userId = context.userId || getCurrentUserId();
      if (userId) {
        scope.setTag('user_id', userId);
      }
      const { accountId, accountName, schoolId, schoolName } = getSentryTrackingUserContext();
      if (accountId) {
        scope.setTag('account_id', String(accountId));
        if (accountName) scope.setTag('account_name', accountName);
      }
      if (schoolId) {
        scope.setTag('school_id', String(schoolId));
        if (schoolName) scope.setTag('school_name', schoolName);
      }

      scope.setContext('business_context', {
        component: context.component,
        action: context.action,
        userId: userId,
        additionalData: context.additionalData,
        accountId,
        accountName,
        schoolId,
        schoolName,
      });
      
      scope.setLevel('error');
      
      const errorToCapture = error instanceof Error ? error : new Error(String(error));
      Sentry.captureException(errorToCapture);
    });
  } catch (sentryError) {
    console.error('[Sentry] Error capturing business logic error:', sentryError);
  }
};

/**
 * Capture async operation errors (like file uploads, API calls, etc.)
 */
export const captureAsyncError = (error: Error | string, context: { operation?: string; component?: string; additionalData?: Record<string, any> } = {}, userId?: string) => {
  try {
    const Sentry = getSentry();
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'async_operation');
      
      // Automatically include current user ID
      const userId = getCurrentUserId();
      if (userId) {
        scope.setTag('user_id', userId);
      }
      const { accountId, accountName, schoolId, schoolName } = getSentryTrackingUserContext();
      if (accountId) {
        scope.setTag('account_id', String(accountId));
        if (accountName) scope.setTag('account_name', accountName);
      }
      if (schoolId) {
        scope.setTag('school_id', String(schoolId));
        if (schoolName) scope.setTag('school_name', schoolName);
      }

      if (context.operation) {
        scope.setTag('operation', context.operation);
      }
      
      if (context.component) {
        scope.setTag('component', context.component);
      }
      
      scope.setContext('async_error', {
        operation: context.operation,
        component: context.component,
        additionalData: context.additionalData,
        userId,
        accountId,
        accountName,
        schoolId,
        schoolName,
      });
      
      scope.setLevel('error');
      
      const errorToCapture = error instanceof Error ? error : new Error(String(error));
      Sentry.captureException(errorToCapture);
    });
  } catch (sentryError) {
    console.error('[Sentry] Error capturing async error:', sentryError);
  }
};

/**
 * Capture form validation errors (for critical form failures)
 */
export const captureFormError = (error: Error | string, context: { formName?: string; fieldName?: string; userId?: string } = {}) => {
  try {
    const Sentry = getSentry();
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'form_error');
      
      if (context.formName) {
        scope.setTag('form_name', context.formName);
      }
      
      if (context.fieldName) {
        scope.setTag('field_name', context.fieldName);
      }
      
      // Use provided userId or automatically get current user ID
      const userId = context.userId || getCurrentUserId();
      if (userId) {
        scope.setTag('user_id', userId);
      }
      const { accountId, accountName, schoolId, schoolName } = getSentryTrackingUserContext();
      if (accountId) {
        scope.setTag('account_id', String(accountId));
        if (accountName) scope.setTag('account_name', accountName);
      }
      if (schoolId) {
        scope.setTag('school_id', String(schoolId));
        if (schoolName) scope.setTag('school_name', schoolName);
      }
      
      scope.setContext('form_error', {
        ...context,
        userId,
        accountId,
        accountName,
        schoolId,
        schoolName,
      });
      scope.setLevel('warning'); // Form errors are typically warnings, not critical errors
      
      const errorToCapture = error instanceof Error ? error : new Error(String(error));
      Sentry.captureException(errorToCapture);
    });
  } catch (sentryError) {
    console.error('[Sentry] Error capturing form error:', sentryError);
  }
};

/**
 * Enhanced console.error that also captures to Sentry
 * Use this for critical errors that should be tracked
 */
export const logAndCaptureError = (message: string, error?: Error | any, context: Record<string, any> = {}, userId?: string) => {
  // Always log to console (use direct console to avoid infinite loops)
  console.error(message, error);
  
  // Also capture to Sentry if we have an actual error
  if (error) {
    try {
      const Sentry = getSentry();
      Sentry.withScope(scope => {
        scope.setTag('error_type', 'logged_error');
        
        // Get user context from session storage
        const userContext = getCurrentUserContext();
        const finalUserId = userId || userContext.userId;
        
        if (finalUserId) {
          scope.setTag('user_id', finalUserId);
        }
        
        // Add account info if available
        if (userContext.accountId) {
          scope.setTag('account_id', String(userContext.accountId));
          if (userContext.accountName) scope.setTag('account_name', userContext.accountName);
        }
        
        // Add school info if available
        if (userContext.schoolId) {
          scope.setTag('school_id', String(userContext.schoolId));
          if (userContext.schoolName) scope.setTag('school_name', userContext.schoolName);
        }
        
        scope.setContext('logged_error_context', {
          message,
          userId: finalUserId,
          accountId: userContext.accountId,
          accountName: userContext.accountName,
          schoolId: userContext.schoolId,
          schoolName: userContext.schoolName,
          ...context
        });
        scope.setLevel('error');
        
        const errorToCapture = error instanceof Error ? error : new Error(String(error));
        Sentry.captureException(errorToCapture);
      });
    } catch (sentryError) {
      console.error('[Sentry] Error capturing logged error:', sentryError);
    }
  }
};

/**
 * Enhanced console.warn that also captures to Sentry for important warnings
 * Use this for warnings that should be tracked but aren't critical errors
 */
export const logAndCaptureWarning = (message: string, data?: any, context: Record<string, any> = {}, userId?: string) => {
  // Always log to console (use direct console to avoid infinite loops)
  console.warn(message, data);
  
  // Capture to Sentry as a message (not an exception)
  try {
    const Sentry = getSentry();
    Sentry.withScope(scope => {
      scope.setTag('warning_type', 'logged_warning');
      
      // Get user context from session storage
      const userContext = getCurrentUserContext();
      const finalUserId = userId || userContext.userId;
      
      if (finalUserId) {
        scope.setTag('user_id', finalUserId);
      }
      
      // Add account info if available
      if (userContext.accountId) {
        scope.setTag('account_id', String(userContext.accountId));
        if (userContext.accountName) scope.setTag('account_name', userContext.accountName);
      }
      
      // Add school info if available
      if (userContext.schoolId) {
        scope.setTag('school_id', String(userContext.schoolId));
        if (userContext.schoolName) scope.setTag('school_name', userContext.schoolName);
      }

      scope.setContext('warning_context', {
        message,
        data,
        userId: finalUserId,
        accountId: userContext.accountId,
        accountName: userContext.accountName,
        schoolId: userContext.schoolId,
        schoolName: userContext.schoolName,
        ...context
      });
      scope.setLevel('warning');
      
      Sentry.captureMessage(message, 'warning');
    });
  } catch (sentryError) {
    console.error('[Sentry] Error capturing warning:', sentryError);
  }
};
